import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableRow, TextField, Typography } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import api from '../api/axios';
import { AuthContext } from '../AuthContext';
import { businessError, useBusinessContext } from '../BusinessContext';
import { canAccessFinance } from '../components/financeAccess';
import PageHeader from '../components/PageHeader';
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';

const currency = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const money = (value, missing = 'Nicht berechenbar') => value == null ? missing : currency.format(value);
const gameRows = [['rotwild', 'Einnahmen Rotwild'], ['schwarzwild', 'Einnahmen Schwarzwild'],
  ['rehwild', 'Einnahmen Rehwild'], ['restliches_wild', 'Einnahmen restliches Wild']];

function ReportTable({ title, rows }) {
  return <Box component="section" sx={{ minWidth: 0 }}>
    <Typography component="h2" variant="h6" sx={{ mb: 1 }}>{title}</Typography>
    <TableContainer component={Paper}>
      <Table size="small" aria-label={title}><TableBody>{rows.map(row =>
        <TableRow key={row.key || row.label} sx={row.total ? { '& th, & td': { fontWeight: 'bold' } } : undefined}>
          <TableCell component="th" scope="row" sx={{ overflowWrap: 'anywhere' }}>{row.label}{row.action}</TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{row.value}</TableCell>
        </TableRow>
      )}</TableBody></Table>
    </TableContainer>
  </Box>;
}

export default function Finanzen() {
  const auth = useContext(AuthContext);
  const permitted = canAccessFinance(auth?.user);
  const { ready, scopeKey, withContext } = useBusinessContext();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const [value, setValue] = useState('');
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const generation = useRef(0);
  const activeScope = useRef(scopeKey);
  activeScope.current = scopeKey;
  const load = useCallback(async () => {
    const job = ++generation.current;
    setReport(null); setError('');
    if (!permitted || !ready) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await api.get(withContext('/finanzen/report'));
      if (job === generation.current) setReport(response.data);
    } catch (failure) {
      if (job === generation.current) setError(businessError(failure, 'Finanzübersicht konnte nicht geladen werden.'));
    } finally { if (job === generation.current) setLoading(false); }
  }, [permitted, ready, withContext]);
  useEffect(() => {
    setEditor(null); setSuccess(false); setEditError(''); setSaving(false);
    load();
    return () => { generation.current += 1; };
  }, [load, scopeKey]);
  const save = async event => {
    event.preventDefault();
    if (!editor || editor.scope !== activeScope.current || saving) return;
    if (!/^-?(?:\d+(?:[.,]\d{1,2})?|\d{1,3}(?:\.\d{3})+,\d{1,2})$/.test(value.trim())) {
      setEditError('Bitte einen gültigen Kontostand eingeben, z. B. 1.234,56.'); return;
    }
    setSaving(true); setEditError('');
    const target = editor;
    try {
      await api.put(target.url, { kontostand: value.trim(), betriebsjahr_id: target.year });
      if (target.scope !== activeScope.current) return;
      setEditor(null); setSuccess(true);
      await load();
    } catch (failure) {
      if (target.scope === activeScope.current) setEditError(businessError(failure, 'Kontostand konnte nicht gespeichert werden.'));
    } finally { if (target.scope === activeScope.current) setSaving(false); }
  };
  if (!permitted) return <Alert severity="error">Kein Zugriff auf die Finanzübersicht.</Alert>;
  const editAction = report && <IconButton size="small" color="primary" aria-label="Kontostand bearbeiten" onClick={() => {
    setValue(report.kasse.kontostand == null ? '' : report.kasse.kontostand.replace('.', ','));
    setEditError('');
    setEditor({ scope: scopeKey, year: report.betriebsjahr.id, url: withContext('/finanzen/kontostand') });
  }}><Edit fontSize="small" /></IconButton>;
  return <Box sx={{ maxWidth: 1200, mx: 'auto', minWidth: 0 }}>
    <PageHeader title="Finanzübersicht" action={<Button size="small" onClick={load} disabled={loading || saving || !ready}>Aktualisieren</Button>} />
    {!ready && <Alert severity="info">Bitte einen vollständigen Geschäftskontext auswählen.</Alert>}
    {loading && <LoadingIndicator label="Finanzübersicht wird geladen" />}
    {error && <LoadError onRetry={load}>{error}</LoadError>}
    {report && <>
      <Typography sx={{ mb: 2 }}>{report.mandant.name} · {report.betrieb.name} · {report.betriebsjahr.name}</Typography>
      {report.hinweise.map(message => <Alert key={message} severity="warning" sx={{ mb: 2 }}>{message}</Alert>)}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
        <ReportTable title="Einnahmen" rows={[
          ...gameRows.map(([key, label]) => ({ label, value: money(report.einnahmen.wild[key]) })),
          ...report.einnahmen.kategorien.map(row => ({ key: row.id, label: row.name, value: money(row.betrag) })),
          { label: 'Einnahmen gesamt', value: money(report.einnahmen.gesamt), total: true },
        ]} />
        <ReportTable title="Ausgaben" rows={[
          ...report.ausgaben.kategorien.map(row => ({ key: row.id, label: row.name, value: money(row.betrag) })),
          { label: 'Ausgaben gesamt', value: money(report.ausgaben.gesamt), total: true },
        ]} />
        <Box sx={{ gridColumn: '1 / -1' }}><ReportTable title="Kassenübersicht" rows={[
          { label: 'Kassenanfangsbestand', value: money(report.kasse.anfangsbestand, 'Nicht erfasst') },
          { label: 'Einnahmen gesamt', value: money(report.kasse.einnahmen), total: true },
          { label: 'Ausgaben gesamt', value: money(report.kasse.ausgaben), total: true },
          { label: 'Aktueller Kassenstand', value: money(report.kasse.kassenstand), total: true },
          { label: 'Aktueller Kontostand', value: money(report.kasse.kontostand, 'Nicht erfasst'), action: editAction },
          { label: 'Barkasse (Sollbestand)', value: money(report.kasse.barkasse_soll), total: true },
        ]} /></Box>
      </Box>
    </>}
    <Dialog open={Boolean(editor)} onClose={() => { if (!saving) setEditor(null); }} fullWidth maxWidth="xs">
      <Box component="form" onSubmit={save}>
        <DialogTitle>Kontostand bearbeiten</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>{report?.betrieb.name} · {report?.betriebsjahr.name}</Typography>
          <TextField autoFocus margin="dense" fullWidth label="Kontostand" value={value} onChange={event => setValue(event.target.value)}
            disabled={saving} error={Boolean(editError)} helperText={editError || 'Euro, z. B. 1.234,56'} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
        </DialogContent>
        <DialogActions><Button disabled={saving} onClick={() => setEditor(null)}>Abbrechen</Button><Button type="submit" disabled={saving} variant="contained">Speichern</Button></DialogActions>
      </Box>
    </Dialog>
    <Snackbar open={success} autoHideDuration={4000} onClose={() => setSuccess(false)}><Alert severity="success">Kontostand gespeichert.</Alert></Snackbar>
  </Box>;
}
